import { useCallback } from "react";
import { z } from "zod";
import { useForm } from "react-hook-form";
import { useTranslation } from "react-i18next";
import { zodResolver } from "@hookform/resolvers/zod";
import { cn } from "~/utils";
import { Text } from "~/components/text";
import { Button } from "~/components/button";
import { Input } from "~/components/ui/input";
import { logError } from "~/shared/services/error-reporting";
import { useAuth } from "~/contexts/auth-context";
import { useAnalytics } from "~/contexts/analytics-context";
import { toast } from "~/utils/toast";
import { LoginLinkSuccessModal } from "./login-link-success-modal";

const schema = z.object({ email: z.email() });

export function LoginForm() {
  const { t } = useTranslation(["general", "login"]);

  const { sendLoginLink } = useAuth();
  const { gaEvent } = useAnalytics();

  const { handleSubmit, register, formState, reset, setError, watch } = useForm(
    {
      defaultValues: { email: "" },
      resolver: zodResolver(schema),
      mode: "onChange",
    },
  );

  const loginWithLink = useCallback(
    async (values: { email: string }) => {
      try {
        await sendLoginLink(values.email);
        gaEvent({ type: "login_request", payload: { method: "link" } });
      } catch (error) {
        logError(error);

        toast.error(
          `${t("login:error.loginFailed")}: ${(error as Error).message}`,
        );

        setError("root", error as Error);
      }
    },
    [sendLoginLink, gaEvent, t, setError],
  );

  const email = watch("email", "");

  return (
    <form className="space-y-4" onSubmit={handleSubmit(loginWithLink)}>
      <div className="space-y-1">
        <Input
          placeholder={t("login:form.email.placeholder")}
          className={cn(
            "h-[47px] rounded-lg",
            formState.errors.email?.message &&
              "border-system-error ring-2 ring-system-error/25",
          )}
          {...register("email", { required: true })}
        />
        {formState.errors.email && (
          <Text variant="errorSmall">{t("login:form.email.error")}</Text>
        )}
      </div>

      <Button
        type="submit"
        size="large"
        disabled={formState.isSubmitting || !formState.isValid}
        className="w-full justify-center h-[47px]"
        label={t("login:form.buttons.submit")}
      />

      <LoginLinkSuccessModal
        email={email}
        open={formState.isSubmitSuccessful}
        onClose={() => reset({ email })}
      />
    </form>
  );
}
